<script setup>
import { ref, onMounted } from 'vue';
import axios from 'axios';
import {Link, router} from '@inertiajs/vue3';
import Modal from "../../components/Modal.vue";

const props = defineProps({
    event: {
        type: Array,
    },
    activity: {
        type: Array,
    },
    organization: {
        type: Array,
    },
});

const dialogVisible = ref(false);
const emailDialogVisible = ref(false);
const data = ref([]);
const loading = ref(true);
const pagination = ref({});
const emails = ref(null);
const notes = ref(null);
const canPublish = ref(false);

const fetchData = async (url = '/ajax/payouts/'+props.activity.id) => {
    loading.value = true;
    try {
        const response = await axios.get(url);
        data.value = response.data.data;
        pagination.value = {
            total: response.data.total,
            per_page: response.data.per_page,
            current_page: response.data.current_page,
            from: response.data.from,
            to: response.data.to,
            last_page: response.data.last_page,
            prev_page_url: response.data.prev_page_url,
            next_page_url: response.data.next_page_url,
        };
        canPublish.value = response.data.canPublish
    } catch (error) {
        console.error('Error fetching data:', error);
    } finally {
        loading.value = false;
    }
};

function publishPayout() {
    axios
        .get('/events/' + props.event.id + '/activities/' + props.activity.id + '/publishPayout')
        .then((response) => {
            props.activity.publish_payout = true;
            closeConfirmModal()
        })
        .catch((error) => {
            console.error('Error occurred:', error);
        });
}

function unPublishPayout() {
    axios
        .get('/events/' + props.event.id + '/activities/' + props.activity.id + '/unPublishPayout')
        .then((response) => {
            props.activity.publish_payout = false;
        })
        .catch((error) => {
            console.error('Error occurred:', error);
        });
}

onMounted(() => {
    fetchData();
});

function formatScore(value) {
    if (isNumber(value)) {
        return Number(value).toFixed(3);
    }
    return value;
}

function isNumber(value) {
    if (value === null || value === '') {
        return true;
    }
    return !isNaN(Number(value.toString()));
}

function savePrize(index, prize) {
    axios.post('/ajax/prize', {
        'id': data.value[index].id,
        'prize': data.value[index].prize,
    })
}

function savePoints(index, points) {
    axios.post('/ajax/points', {
        'id': data.value[index].id,
        'points': points,
    })
}

function closeConfirmModal() {
    dialogVisible.value = false
}

function closeEmailModal() {
    emailDialogVisible.value = false
    emails.value = null;
    notes.value = null;
}

function sendEmails() {
    axios.post('/api/hal/email-results/' + props.activity.id, {
        'emails': emails.value,
        'notes': notes.value,
    })

    closeEmailModal();
}

function openWindow(url) {
    if (typeof window !== 'undefined') {
        window.open(
            url,
            '_blank',
            'noopener noreferrer'
        );
    }
}
</script>

<template>
    <div>
        <div class="card">
            <div class="card-header">
                <h2 style="margin-bottom: 0;">
                    <a :href="'/organizer/organizations/'+organization.id">
                        {{ organization.name }}
                    </a>
                    &gt;&gt;
                    <a
                        :href="'/organizer/organizations/' + organization.id + '/events/' + event.id + '/activities'">
                        {{ event.name }}
                    </a>
                    &gt;&gt;
                    <span class='activity_name'>
                        {{ activity.name }}
                    </span>
                </h2>
            </div>
        </div>
        <br>

        <div class="text-center">
            <div class="btn-group">

                <button v-if="!activity?.publish_payout && canPublish" id="Publish_payout" class="btn btn-primary action-button"
                        @click="dialogVisible=true">
                    Publish Payout
                </button>
                <Modal :visible="dialogVisible">
                    <template v-slot:header><h3 class="mb-0">Publish Payout</h3></template>
                    <template v-slot:body>
                        <p class="mb-0">You're about to post the payout live to the mobile app. Registered users in your event will be notified. Are you sure you're ready to post the payout?</p>
                    </template>
                    <template v-slot:footer>
                        <button class="btn btn-outline-dark" @click="closeConfirmModal">Cancel</button>
                        <button class="btn btn-primary" @click="publishPayout">Confirm</button>
                    </template>
                </Modal>

                <button v-if="activity?.publish_payout" id="unpublish_payout" class="btn btn-primary action-button"
                        @click="unPublishPayout">
                    Unpublish Payout
                </button>

                <button class="btn action-button"
                        :class="{'btn-secondary': !activity?.publish_payout, 'btn-primary': activity?.publish_payout}"
                        @click="openWindow('/payout/activity/' + activity.id)"
                        :disabled="!activity?.publish_payout"
                >
                    Print Results
                </button>

                <button id="email_results" class="btn action-button"
                        :class="{'btn-secondary': !activity?.publish_payout, 'btn-primary': activity?.publish_payout}"
                        @click="emailDialogVisible=true" :disabled="!activity?.publish_payout">
                    Report
                </button>
                <Modal :visible="emailDialogVisible">
                    <template v-slot:header><h3 class="mb-0">Email Results</h3></template>
                    <template v-slot:body>
                        <p class="mb-0">Email a link of the results of this event!<br>Enter a comma separated list of emails and along with a note below.</p>
                        Emails:
                        <textarea v-model="emails" class="form-control"></textarea>
                        Notes:
                        <textarea v-model="notes" class="form-control"></textarea>
                    </template>
                    <template v-slot:footer>
                        <button class="btn btn-outline-dark" @click="closeEmailModal">Cancel</button>
                        <button class="btn btn-primary" @click="sendEmails">Send</button>
                    </template>
                </Modal>

                <button class="btn action-button"
                        :class="{'btn-secondary': !activity?.publish_payout, 'btn-primary': activity?.publish_payout}"
                        @click="openWindow(`/organizer/scoring/${props.event.id}/activities/${props.activity.id}/print-checks`)"
                        :disabled="!activity?.publish_payout"
                >
                    Print Checks
                </button>

            </div>
        </div>

        <br>

        <div class="bg-white px-3 py-5 w-100 mt-3">
            <!-- Spinner -->
            <div v-if="loading" class="d-flex justify-content-center align-items-center mx-auto">
                <p class="mb-0 mr-2">Loading...</p>
                <svg xmlns="http://www.w3.org/2000/svg" width="3em" height="3em" viewBox="0 0 24 24">
                    <circle cx="4" cy="12" r="1.5" fill="#24AAE0"><animate attributeName="r" dur="0.75s" repeatCount="indefinite" values="1.5;3;1.5"/></circle>
                    <circle cx="12" cy="12" r="3" fill="#24AAE0"><animate attributeName="r" dur="0.75s" repeatCount="indefinite" values="3;1.5;3"/></circle>
                    <circle cx="20" cy="12" r="1.5" fill="#24AAE0"><animate attributeName="r" dur="0.75s" repeatCount="indefinite" values="1.5;3;1.5"/></circle>
                </svg>
            </div>

            <div v-else class="table-responsive w-100">
                <table class="table table-striped results w-100">
                    <thead>
                        <tr>
                            <th class="text-left" style="max-width: 70px;">Draw</th>
                            <th class="text-left">Rider</th>
                            <th v-if="activity.track_horses" class="text-left">Horse</th>
                            <th v-if="activity.track_bulls" class="text-left">Bull</th>
                            <th v-if="event.category == 'Barrel Racing'" class="text-left">BBR #</th>
                            <th class="text-left">Hometown</th>
                            <th class="text-center">Time</th>
                            <th v-if="activity.has_divisions" class="text-center">Division</th>
                            <th class="text-center">Earnings</th>
                            <th v-if="activity.points_seasons_group_id" class="text-center">Points</th>
                        </tr>
                    </thead>

                    <tbody>
                        <tr v-for="(score, index) in data" :class="{ 'point-group-highlight': score.userActivity.user.in_activity_point_group }">
                            <td class="text-left">
                                <span v-if="score.userActivity.root_activity_id">CO</span>
                                <span v-else>{{ score.userActivity.position }}</span>
                            </td>
                            <td class="text-left">
                                <span v-if="score.userActivity.user.not_real_users">
                                    {{ score.userActivity.user.name }}
                                </span>
                                <span v-else>
                                    <a target="_blank" :href="'/users/view/' + score.userActivity.user.id">{{ score.userActivity.user.name }}</a>
                                </span>
                            </td>
                            <td v-if="activity.track_horses" class="text-left">{{ score.userActivity.horse.name }}</td>
                            <th v-if="activity.track_bulls" class="text-left">{{ score.userActivity.bull.name }}</th>
                            <th v-if="event.category == 'Barrel Racing'" class="text-left">{{ score.userActivity.user.bbr_id }}</th>
                            <td class="text-left">{{ score.userActivity.user.hometown }}</td>
                            <td class="text-center">{{ formatScore(score.score) }}</td>
                            <td v-if="activity.has_divisions" class="text-center">
                                {{ score.division }}
                            </td>
                            <td class="text-center">
                                <span v-if="score.prize > 0 && activity.payType != 'custom'">
                                    {{ score.prize }}
                                </span>
                                <div v-if="activity.payType == 'custom'" class="input-group input-group-sm"
                                     style="width: 140px; margin: 2px auto; height: 24px; padding: 0 8px;">
                                    <div class="input-group-prepend"><span class="input-group-text" style="padding: 0 8px;">$</span>
                                    </div>
                                    <input type="number" class="form-control form-control-sm" style="height: 24px"
                                           v-model="score.prize"
                                           :tabindex="index+1"
                                           @keyup.enter="savePrize(index, score.prize)"
                                           @blur="savePrize(index, score.prize)"
                                    />
                                </div>
                            </td>
                            <td v-if="activity.points_seasons_group_id" class="text-center">
                                <span v-if="activity.publish_payout">
                                    {{ score.points }}
                                </span>
                                <input v-else type="number"
                                       class="form-control form-control-sm mx-auto"
                                       style="height: 24px;width: 140px;"
                                       v-model="score.points"
                                       :tabindex="index+1"
                                       @keyup.enter="savePoints(index, score.points)"
                                       @blur="savePoints(index, score.points)"
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>

                <!-- Pagination -->
                <nav v-if="pagination.total > pagination.per_page" class="d-flex justify-content-end align-items-center paging">
                    <span class="mr-2">{{ pagination.current_page }} - {{ pagination.last_page }} of {{ pagination.last_page }}</span>
                    <!-- Previous Page -->
                    <button
                        @click="fetchData(pagination.prev_page_url)"
                        class="btn-paging"
                        :disabled="pagination.from == 1"
                        aria-label="Previous page"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" style="height: 18px;width: 18px">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>

                    <!-- Next Page -->
                    <button
                        @click="fetchData(pagination.next_page_url)"
                        class="btn-paging"
                        :disabled="pagination.current_page == pagination.last_page"
                        aria-label="Next page"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" style="height: 18px;width: 18px">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </nav>

            </div>
        </div>
    </div>
</template>

<style scoped>
.table td, .table th {
    padding: 0;
    border-top: 1px solid #dee2e6;
}
.table th {
    border-top: none;
    border-bottom: 1px solid #dee2e6;
}
table td {
    color: #000;
    font-weight: 400;
    font-size: 13px;
}
.btn-outline-dark {
    color: #1e282c;
    border: 1px solid #1e282c;
}
.btn-primary:hover, .btn-primary:focus {
    background: #fff !important;
    border: 1px solid #2199c9 !important;
    color: #2199c9 !important;
}
.btn-outline-dark:hover {
    color: #fff;
    background-color: #1e282c;
    border-color: #1e282c;
}
.btn-paging {
    border: none;
    background: none;
    line-height: 1;
    color: rgba(0, 0, 0, .26);
}
.btn-paging:disabled {
    opacity: 50%;
}
.paging {
    margin: 20px 0 10px 0;
    color: rgba(0, 0, 0, .54);
}

.point-group-highlight {
    background-color: rgba(209, 108, 230, .25);
}
</style>
